/* =========================================================================
   Easter-egg overlays (Saintly Sneeze, Holy Coffee, Sleeping Angel) + the
   source popover markup. Typed port of the prototype — identical timing/DOM.
   Award side-effects go through the shared context (engine + bank UI).
   ========================================================================= */
import { byId } from './dom';
import { ctx } from './context';
import { icon } from './ui/icons';
import { fmtDelta } from './engine';
import { sndAchoo, sndChoir, sndSnore, sndCorrect, tone } from './audio';
import { nextDykFact } from './data/dykData';
import { getLine } from './characters/registry';
import type { FeastMoment } from './api';
import {
  feastAccent,
  feastEmphasis,
  feastAutoDismissMs,
  feastAnnouncementBody,
} from './feastAnnouncement';

/** Shipped Theo art for the scene overlays (public/media/characters). */
const THEO_GREET = 'media/characters/theo-greet.png';
const THEO_PRAY = 'media/characters/theo-pray.png';

/** A 6px gold dot — the particle the sparkle/steam/drip spawners scatter
    (replaced the emoji confetti). Inline-styled: overlays own no CSS here. */
const PUFF_DOT =
  '<span class="puff-dot" style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#DE9F00"></span>';

export const overlaysHTML = /* html */ `
<!-- SAINTLY SNEEZE (easter egg overlay) -->
<div id="sneezeOverlay" class="sneeze hidden" aria-hidden="true">
  <div class="sneeze-dim"></div>
  <div class="sneeze-stage">
    <div class="sneeze-halo">${icon('sparkle', 28)}</div>
    <div class="sneeze-saint char-art tier-1"><img class="char-art-media" src="${THEO_GREET}" alt="" /></div>
    <div class="sneeze-sparkles" id="sneezeSparkles"></div>
    <div class="sneeze-puff">${PUFF_DOT}${PUFF_DOT}${PUFF_DOT}</div>
    <div class="sneeze-text">Bless you!</div>
    <div class="sneeze-sub">Even saints need blessings. <span class="sneeze-amt" id="sneezeAmt">+$75</span></div>
  </div>
</div>

<!-- HOLY COFFEE (special wheel result overlay) -->
<div id="coffeeOverlay" class="holycoffee hidden" aria-hidden="true">
  <div class="hc-dim"></div>
  <div class="hc-stage">
    <div class="hc-steam" id="hcSteam"></div>
    <div class="hc-mug char-art tier-1"><img class="char-art-media" src="${THEO_GREET}" alt="" /><span class="hc-cross">${icon('candle', 18)}</span></div>
    <div class="hc-overflow" id="hcOverflow"></div>
    <div class="hc-wink">${icon('sparkle', 22)}</div>
    <div class="hc-text">Holy Coffee</div>
    <div class="hc-sub">A short rest. Have a cup. <span class="hc-amt" id="hcAmt">+$150</span></div>
  </div>
</div>

<!-- SLEEPING ANGEL (time-out gag overlay) -->
<div id="angelOverlay" class="angel hidden" aria-hidden="true">
  <div class="angel-dim"></div>
  <div class="angel-stage">
    <div class="angel-scene">
      <span class="angel-fig char-art tier-1" id="angelFig"><img class="char-art-media" src="${THEO_PRAY}" alt="" /></span>
      <span class="angel-zzz">z z z</span>
      <span class="angel-glass" id="angelGlass">${icon('clock', 22)}</span>
    </div>
    <div class="angel-text">Brother Theo dozed off.</div>
    <div class="angel-sub">Time's up. <i>"Forgive me. I nodded off."</i></div>
  </div>
</div>

<!-- SOURCE POPOVER (opened by the ⓘ icon on the answer reveal) -->
<div id="sourcePop" class="source-pop hidden" aria-hidden="true">
  <div class="sp-dim"></div>
  <div class="sp-card">
    <div class="sp-kicker">${icon('book', 16)} Source</div>
    <div class="sp-answer" id="spAnswer"></div>
    <div class="sp-cite" id="spCite"></div>
    <div class="sp-close">Tap anywhere to continue</div>
  </div>
</div>

<!-- DID YOU KNOW (mid-round learning interrupt: giant animated ? → fact card) -->
<div id="dykOverlay" class="dyk hidden" aria-hidden="true">
  <div class="dyk-dim"></div>
  <div class="dyk-stage">
    <div class="dyk-mark" id="dykMark">?</div>
    <div class="dyk-card" id="dykCard">
      <div class="dyk-kicker">Did you know</div>
      <div class="dyk-fact" id="dykFact"></div>
      <div class="dyk-cite" id="dykCite"></div>
      <div class="dyk-close">Tap anywhere to continue</div>
    </div>
  </div>
</div>

<!-- FEAST ANNOUNCEMENT ("today is a feast day" interrupt — its own graphic pack:
     a liturgical-colored ray burst + swinging bell medallion → celebratory card.
     Deliberately NOT a reskin of #dykOverlay; separate element, separate .fa-* classes. -->
<div id="feastAnnOverlay" class="feastann hidden" aria-hidden="true" data-accent="gold" data-emphasis="standard">
  <div class="fa-dim"></div>
  <div class="fa-stage">
    <div class="fa-medallion">
      <span class="fa-bell">${icon('bell', 40)}</span>
      <div class="fa-sparks" id="faSparks"></div>
    </div>
    <div class="fa-card">
      <div class="fa-kicker">Today the Church celebrates</div>
      <div class="fa-title" id="faTitle"></div>
      <div class="fa-rank hidden" id="faRank"></div>
      <div class="fa-story" id="faStory"></div>
      <div class="fa-theo"><span class="fa-theo-mark">${icon('candle', 16)}</span><span id="faTheo"></span></div>
      <div class="fa-close">Tap anywhere to continue</div>
    </div>
  </div>
</div>

<!-- REGISTER PROMPT (one-time guest nudge after 20 questions) -->
<div id="regPromptOverlay" class="regprompt hidden" aria-hidden="true">
  <div class="rp-dim"></div>
  <div class="rp-card">
    <div class="rp-emoji">${icon('sparkle', 32)}</div>
    <div class="rp-title">Nice run.</div>
    <div class="rp-body">Want to save your progress? Create a free account to keep your bank, medals &amp; streaks.</div>
    <button class="btn" id="regPromptCreate">Create free account</button>
    <button class="rp-quiet" id="regPromptGuest">Continue as guest</button>
  </div>
</div>`;

/* ---------- Saintly Sneeze ---------- */
export const SNEEZE_CHANCE = 0.12; // rare surprise after a non-jackpot spin
export const SNEEZE_BONUS = 7_500; // $75 manna bonus

export function triggerSneeze(done?: () => void): void {
  const ov = byId('sneezeOverlay');
  ov.classList.remove('show', 'achoo');
  const sp = byId('sneezeSparkles');
  sp.innerHTML = '';
  for (let i = 0; i < 12; i++) {
    const s = document.createElement('div');
    s.className = 'spark';
    s.innerHTML = PUFF_DOT;
    const ang = (i / 12) * Math.PI * 2,
      dist = 70 + Math.random() * 80;
    s.style.setProperty('--tx', (Math.cos(ang) * dist).toFixed(0) + 'px');
    s.style.setProperty('--ty', (Math.sin(ang) * dist - 20).toFixed(0) + 'px');
    s.style.setProperty('--rot', (Math.random() * 360 - 180).toFixed(0) + 'deg');
    s.style.animationDelay = (Math.random() * 0.12).toFixed(2) + 's';
    sp.appendChild(s);
  }
  byId('sneezeAmt').textContent = fmtDelta(SNEEZE_BONUS);
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
  setTimeout(() => {
    sndAchoo();
    ov.classList.add('achoo');
  }, 850);
  setTimeout(() => {
    sndChoir();
  }, 1200);
  setTimeout(() => {
    ctx.engine?.awardBonus(SNEEZE_BONUS, 'Bless you!', 'Sneeze');
    ctx.addAchievement('🤧', 'Bless You!', 'Caught the rare saintly sneeze blessing');
    ctx.updateBank();
    ctx.floatDelta(SNEEZE_BONUS);
  }, 1250);
  setTimeout(() => {
    ov.classList.add('hidden');
    ov.classList.remove('show', 'achoo');
    ov.setAttribute('aria-hidden', 'true');
    if (done) done();
  }, 2900);
}

/* ---------- Holy Coffee ---------- */
export function triggerCoffee(cents: number, done?: () => void): void {
  const ov = byId('coffeeOverlay');
  ov.classList.remove('show');
  const st = byId('hcSteam');
  st.innerHTML = '';
  for (let i = 0; i < 5; i++) {
    const p = document.createElement('div');
    p.className = 'hc-puff';
    p.innerHTML = PUFF_DOT;
    p.style.left = (i - 2) * 16 + 'px';
    p.style.setProperty('--sx', (Math.random() * 40 - 20).toFixed(0) + 'px');
    p.style.animationDelay = (i * 0.3 + Math.random() * 0.2).toFixed(2) + 's';
    st.appendChild(p);
  }
  const of = byId('hcOverflow');
  of.innerHTML = '';
  for (let i = 0; i < 10; i++) {
    const d = document.createElement('div');
    d.className = 'drip';
    d.innerHTML = PUFF_DOT;
    d.style.setProperty('--dx', (Math.random() * 120 - 60).toFixed(0) + 'px');
    d.style.animationDelay = (0.3 + Math.random() * 0.8).toFixed(2) + 's';
    of.appendChild(d);
  }
  byId('hcAmt').textContent = fmtDelta(cents);
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
  setTimeout(() => {
    ov.classList.add('hidden');
    ov.classList.remove('show');
    ov.setAttribute('aria-hidden', 'true');
    if (done) done();
  }, 3200);
}

/* ---------- Did You Know? (learning interrupt) ----------
   A giant gold "?" bounces in and wobbles, then flips away as the fact card
   rises. Tap anywhere to continue (or it auto-continues after 12s). */
export function triggerDidYouKnow(done: () => void): void {
  const ov = byId('dykOverlay');
  const f = nextDykFact();
  byId('dykFact').textContent = f.fact;
  byId('dykCite').textContent = f.citation;
  ov.classList.remove('show', 'reveal', 'hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');            // the big ? bounces in + wobbles
  tone(660, 0.12, 'triangle', 0.06);
  tone(880, 0.14, 'triangle', 0.06, 0.12);
  const revealT = setTimeout(() => {   // ? flips away, card rises
    ov.classList.add('reveal');
    sndCorrect();
  }, 1400);

  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    clearTimeout(revealT);
    clearTimeout(autoT);
    ov.classList.add('hidden');
    ov.classList.remove('show', 'reveal');
    ov.setAttribute('aria-hidden', 'true');
    ov.removeEventListener('pointerdown', finish);
    done();
  };
  const autoT = setTimeout(finish, 12_000);
  ov.addEventListener('pointerdown', finish);
}

/* ---------- Feast announcement ("today is a feast day") ----------
   Same INTERACTION as Did You Know — a full-screen interrupt that reveals a card
   and continues on tap-anywhere or after an auto-dismiss — but a deliberately
   different graphic pack: a liturgical-colored ray burst behind a swinging bell
   medallion that rings, then hands off to a warm feast card. Theo's voice comes
   from the registry's existing 'feast-intro' moment, so the copy stays his and
   nothing here is hardcoded to a particular feast.

   Gating lives entirely in feastAnnouncement.ts / gameController — by the time
   this runs, the day is already known to be a feast worth announcing. */
export function triggerFeastAnnouncement(m: FeastMoment, done: () => void): void {
  const ov = byId('feastAnnOverlay');
  const emphasis = feastEmphasis(m.feastRank);
  ov.dataset.accent = feastAccent(m.color);        // CSS themes off the liturgical color
  ov.dataset.emphasis = emphasis;                  // a Solemnity/Feast gets the fuller treatment
  byId('faTitle').textContent = m.title;
  byId('faStory').textContent = feastAnnouncementBody(m);
  byId('faTheo').textContent = getLine('theo', 'feast-intro');

  const rank = byId('faRank');
  const rankText = (m.feastRank || '').trim();
  rank.textContent = rankText;
  rank.classList.toggle('hidden', rankText === '');

  // A ring of sparks around the medallion — more of them on a solemnity.
  const sp = byId('faSparks');
  sp.innerHTML = '';
  const n = emphasis === 'solemn' ? 12 : 8;
  for (let i = 0; i < n; i++) {
    const s = document.createElement('div');
    s.className = 'fa-spark';
    s.innerHTML = PUFF_DOT;
    const ang = (i / n) * Math.PI * 2;
    const dist = 74 + Math.random() * 34;
    s.style.setProperty('--fx', (Math.cos(ang) * dist).toFixed(0) + 'px');
    s.style.setProperty('--fy', (Math.sin(ang) * dist).toFixed(0) + 'px');
    s.style.animationDelay = (0.25 + Math.random() * 0.5).toFixed(2) + 's';
    sp.appendChild(s);
  }

  ov.classList.remove('show', 'reveal', 'hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show'); // rays bloom, the bell swings in and rings
  tone(784, 0.16, 'sine', 0.06);
  tone(1046, 0.2, 'sine', 0.05, 0.16);

  const revealT = setTimeout(() => { // the bell lifts away, the feast card rises
    ov.classList.add('reveal');
    if (emphasis === 'solemn') sndChoir();
    else sndCorrect();
  }, 1500);

  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    clearTimeout(revealT);
    clearTimeout(autoT);
    ov.classList.add('hidden');
    ov.classList.remove('show', 'reveal');
    ov.setAttribute('aria-hidden', 'true');
    ov.removeEventListener('pointerdown', finish);
    done();
  };
  const autoT = setTimeout(finish, feastAutoDismissMs(emphasis));
  ov.addEventListener('pointerdown', finish);
}

/* ---------- Register prompt (one-time guest nudge) ----------
   A warm modal in the game's voice. Two paths: primary "Create free account"
   and a quiet "Continue as guest". The controller supplies both handlers and is
   responsible for the mid-game auth round-trip / resume. */
export function openRegisterPrompt(onCreate: () => void, onGuest: () => void): void {
  const ov = byId('regPromptOverlay');
  const create = byId<HTMLButtonElement>('regPromptCreate');
  const guest = byId<HTMLButtonElement>('regPromptGuest');
  create.onclick = () => {
    closeRegisterPrompt();
    onCreate();
  };
  guest.onclick = () => {
    closeRegisterPrompt();
    onGuest();
  };
  ov.classList.remove('hidden', 'show');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
}

export function closeRegisterPrompt(): void {
  const ov = byId('regPromptOverlay');
  ov.classList.add('hidden');
  ov.classList.remove('show');
  ov.setAttribute('aria-hidden', 'true');
}

/* ---------- Sleeping Angel (time-out gag) ---------- */
export function triggerAngel(done?: () => void): void {
  const ov = byId('angelOverlay');
  const glass = byId('angelGlass');
  ov.classList.remove('show', 'wake');
  glass.innerHTML = icon('clock', 22);
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
  sndSnore();
  setTimeout(sndSnore, 800);
  setTimeout(() => {
    ov.classList.add('wake');
    glass.innerHTML = icon('bell', 22);
    tone(520, 0.08, 'triangle', 0.06, 0);
    tone(780, 0.1, 'triangle', 0.06, 0.09);
  }, 1600);
  setTimeout(() => {
    ov.classList.add('hidden');
    ov.classList.remove('show', 'wake');
    ov.setAttribute('aria-hidden', 'true');
    if (done) done();
  }, 3400);
}
